import { memo, useEffect, useState } from 'react'
import type { AgentToolCall } from '@multiplayer-app/ai-agent-types'
import { AgentToolCallStatus } from '@multiplayer-app/ai-agent-types'
import { Bot, Check, ChevronDown, ChevronUp, Loader2, X } from 'lucide-react'
import { useAgentStore } from '@/context/AgentProvider'
import { getChatState } from '@/state/useAgentStore'
import { SubagentChatPreview } from './SubagentChatPreview'

interface SubagentToolCallProps {
  call: AgentToolCall
  subagentChatId: string
}

const getStatusMeta = (status: AgentToolCallStatus) => {
  switch (status) {
    case AgentToolCallStatus.Succeeded:
      return { key: 'succeeded', label: 'Done', icon: <Check size={12} /> }
    case AgentToolCallStatus.Failed:
      return { key: 'failed', label: 'Failed', icon: <X size={12} /> }
    case AgentToolCallStatus.Pending:
      return { key: 'running', label: 'Preparing', icon: <Loader2 size={12} className='mp-agent-subagent-spinner' /> }
    default:
      return { key: 'running', label: 'Running', icon: <Loader2 size={12} className='mp-agent-subagent-spinner' /> }
  }
}

/**
 * Minimal expandable subagent thread, rendered below a tool call's own output:
 * a borderless full-width row (icon + subagent name + status, chevron on the
 * right edge) with the nested conversation underneath when expanded.
 */
export const SubagentToolCall = memo(({ call, subagentChatId }: SubagentToolCallProps) => {
  // Collapsed by default — even mid-run. The status chip shows progress, and
  // the nested transcript (plus its message fetch) only happens on expand.
  const [isExpanded, setIsExpanded] = useState(false)

  // The subagent's display name comes from its chat messages once they load
  // (the store is fed by any useMessagesInfinite consumer); tool names stay
  // out of the UI — before the name resolves the row just reads "Subagent".
  const subagentName = useAgentStore((state) => {
    const { messages, pendingAssistant } = getChatState(state, subagentChatId)
    const agentMessage =
      messages.find((message) => message.agentName || message.sender?.type === 'agent') ?? pendingAssistant
    return agentMessage?.agentName || agentMessage?.sender?.displayName || undefined
  })

  // A nested tool call inside the subagent chat is waiting on the user
  // (approval buttons or a clarification form) and hasn't been answered yet.
  const awaitsUserAction = useAgentStore((state) => {
    const { messages, pendingAssistant } = getChatState(state, subagentChatId)
    const allMessages = pendingAssistant ? [...messages, pendingAssistant] : messages
    return allMessages.some((message) =>
      message.toolCalls?.some(
        (toolCall) =>
          (toolCall.requiresConfirmation || toolCall.requiresUserAction) &&
          toolCall.approved === undefined &&
          toolCall.status !== AgentToolCallStatus.Succeeded &&
          toolCall.status !== AgentToolCallStatus.Failed
      )
    )
  })

  const isSettled = call.status === AgentToolCallStatus.Succeeded || call.status === AgentToolCallStatus.Failed

  // Auto-expand so the approval UI is visible; the user can still collapse
  // manually — this only re-fires when a new user-action request appears.
  useEffect(() => {
    if (awaitsUserAction && !isSettled) setIsExpanded(true)
  }, [awaitsUserAction, isSettled])

  const status = getStatusMeta(call.status)

  return (
    <div className='mp-agent-subagent'>
      <button
        className='mp-agent-subagent-row'
        type='button'
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <span className='mp-agent-subagent-row-icon'>
          <Bot size={14} />
        </span>
        <span className='mp-agent-subagent-row-title'>
          {subagentName ? (
            <>
              <span className='mp-agent-subagent-row-label'>Subagent:</span> {subagentName}
            </>
          ) : (
            'Subagent'
          )}
        </span>
        <span className={`mp-agent-subagent-row-status is-${status.key}`}>
          {status.icon}
          {status.label}
        </span>
        <span className='mp-agent-subagent-row-chevron'>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      <SubagentChatPreview chatId={subagentChatId} isExpanded={isExpanded} />
    </div>
  )
})

SubagentToolCall.displayName = 'SubagentToolCall'
